import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Send, Facebook, Instagram, Youtube, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import PageHero from '../components/PageHero';

const Contact = () => {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/settings/`);
      const data = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast({
          title: 'تم إرسال الرسالة بنجاح',
          description: 'سنتواصل معك في أقرب وقت',
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
      } else {
        toast({
          title: 'خطأ في الإرسال',
          description: 'حدث خطأ أثناء إرسال الرسالة، يرجى المحاولة مرة أخرى',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast({
        title: 'خطأ في الإرسال',
        description: 'حدث خطأ أثناء إرسال الرسالة، يرجى المحاولة مرة أخرى',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="min-h-screen">
      <PageHero 
        title="اتصل بنا"
        subtitle="نحن هنا للإجابة على استفساراتكم ومساعدتكم"
        backgroundImage={settings?.page_contact_bg}
      />

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="space-y-6">
              {/* Phone Card */}
              <Card className="border-t-4 border-t-[#0F388E] hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#0F388E] rounded-full flex items-center justify-center mb-4">
                    <Phone className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F388E] dark:text-white mb-2">الهاتف</h3>
                  {settings?.contact_settings?.phone && (
                    <a href={`tel:${settings.contact_settings.phone.replace(/[^\d+]/g, '')}`} className="text-gray-600 dark:text-gray-300 hover:text-[#0F388E] dark:hover:text-[#C9A040] hover:underline transition-colors block" dir="ltr">
                      {settings.contact_settings.phone}
                    </a>
                  )}
                  {settings?.contact_settings?.phone_secondary && (
                    <a href={`tel:${settings.contact_settings.phone_secondary.replace(/[^\d+]/g, '')}`} className="text-gray-600 dark:text-gray-300 hover:text-[#0F388E] dark:hover:text-[#C9A040] hover:underline transition-colors block mt-2" dir="ltr">
                      {settings.contact_settings.phone_secondary}
                    </a>
                  )}
                </CardContent>
              </Card>

              {/* Email Card */}
              <Card className="border-t-4 border-t-[#C9A040] hover:shadow-xl transition-all dark:bg-[#0E2243] dark:border-gray-800">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#C9A040] rounded-full flex items-center justify-center mb-4">
                    <Mail className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F388E] dark:text-white mb-2">البريد الإلكتروني</h3>
                  {settings?.contact_settings?.email && (
                    <a href={`mailto:${settings.contact_settings.email}`} className="text-gray-600 dark:text-gray-300 hover:text-[#0F388E] dark:hover:text-[#C9A040] hover:underline transition-colors block">
                      {settings.contact_settings.email}
                    </a>
                  )}
                </CardContent>
              </Card>

              {/* Address Card */}
              <Card className="border-t-4 border-t-[#0F388E] hover:shadow-xl transition-all dark:bg-[#0E2243] dark:border-gray-800">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-[#0F388E] rounded-full flex items-center justify-center mb-4">
                    <MapPin className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F388E] dark:text-white mb-2">العنوان</h3>
                  {settings?.contact_settings?.address_ar && (
                    <a 
                      href={settings?.contact_settings?.map_url || settings?.contact_settings?.map_link || 'https://maps.app.goo.gl/M8dLpS6wdmQdVHB49'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-600 dark:text-gray-300 hover:text-[#0F388E] dark:hover:text-[#C9A040] hover:underline transition-colors block leading-relaxed"
                      title="فتح موقع الجامعة على خرائط Google"
                    >
                      {settings.contact_settings.address_full_ar || settings.contact_settings.address_ar}
                    </a>
                  )}
                </CardContent>
              </Card>

              {/* Social Media Card */}
              <Card className="border-t-4 border-t-[#C9A040] hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#0F388E] mb-4">تابعنا على</h3>
                  <div className="flex flex-wrap gap-3">
                    {settings?.contact_settings?.whatsapp && (
                      <a
                        href={settings.contact_settings.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                        title="واتساب"
                      >
                        <MessageCircle className="w-6 h-6 text-white" />
                      </a>
                    )}
                    {settings?.contact_settings?.facebook && (
                      <a
                        href={settings.contact_settings.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-[#1877F2] rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                        title="فيسبوك"
                      >
                        <Facebook className="w-6 h-6 text-white" />
                      </a>
                    )}
                    {settings?.contact_settings?.instagram && (
                      <a
                        href={settings.contact_settings.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                        title="إنستقرام"
                      >
                        <Instagram className="w-6 h-6 text-white" />
                      </a>
                    )}
                    {settings?.contact_settings?.twitter && (
                      <a
                        href={settings.contact_settings.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-black rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                        title="X (تويتر)"
                      >
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </svg>
                      </a>
                    )}
                    {settings?.contact_settings?.youtube && (
                      <a
                        href={settings.contact_settings.youtube}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 bg-[#FF0000] rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                        title="يوتيوب"
                      >
                        <Youtube className="w-6 h-6 text-white" />
                      </a>
                    )}
                    {settings?.contact_settings?.email && (
                      <a
                        href={`mailto:${settings.contact_settings.email}`}
                        className="w-12 h-12 bg-[#0F388E] rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                        title="البريد الإلكتروني"
                      >
                        <Mail className="w-6 h-6 text-white" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-2">
              <Card className="shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#0F388E] mb-6">أرسل رسالة</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">الاسم *</label>
                        <Input type="text" name="name" value={formData.name} onChange={handleChange} required placeholder="أدخل اسمك" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">البريد الإلكتروني *</label>
                        <Input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="example@email.com" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">رقم الهاتف *</label>
                        <Input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="+967 XXX XXX XXX" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">الموضوع *</label>
                        <Input type="text" name="subject" value={formData.subject} onChange={handleChange} required placeholder="موضوع الرسالة" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">الرسالة *</label>
                      <Textarea name="message" value={formData.message} onChange={handleChange} required rows={6} placeholder="اكتب رسالتك هنا..." />
                    </div>
                    <Button type="submit" size="lg" className="w-full bg-[#C9A040] hover:bg-[#C9A040]/90 text-white">
                      إرسال الرسالة
                      <Send className="mr-2 w-5 h-5" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Map Section */}
          {settings?.contact_settings?.map_latitude && settings?.contact_settings?.map_longitude && (
            <div className="mt-12">
              <Card className="overflow-hidden border border-gray-200 dark:border-gray-800 shadow-lg">
                <CardContent className="p-0">
                  <iframe
                    title="موقع الجامعة"
                    src={`https://maps.google.com/maps?q=${settings.contact_settings.map_latitude},${settings.contact_settings.map_longitude}&z=${settings.contact_settings.map_zoom || '15'}&output=embed`}
                    width="100%"
                    height="450"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="w-full"
                  ></iframe>
                  <div className="p-4 bg-white dark:bg-[#0E2243] flex flex-col sm:flex-row justify-between items-center gap-4 border-t dark:border-gray-800">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      📍 {settings.contact_settings.address_full_ar || settings.contact_settings.address_ar}
                    </span>
                    <a
                      href={settings?.contact_settings?.map_url || settings?.contact_settings?.map_link || 'https://maps.app.goo.gl/M8dLpS6wdmQdVHB49'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#0F388E] hover:bg-[#123985] text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-all shadow hover:shadow-md"
                    >
                      <MapPin className="w-4 h-4 text-[#C9A040]" />
                      فتح في تطبيق خرائط Google
                    </a>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Contact;